import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui"
import { BarChart2, Layers, Sparkles, Zap } from "lucide-react"
import { useTranslation } from "react-i18next"

export interface CreateOpportunitySetCardProps {
  onClick?: () => void
  canCreate?: boolean
  isCreatingSet?: boolean
  isGenerating?: boolean
  /** When false, hides the bottom "+ Create Opportunity Set" action row. */
  showAction?: boolean
}

export function CreateOpportunitySetCard({
  onClick,
  canCreate = true,
  isCreatingSet,
  isGenerating,
  showAction = true,
}: CreateOpportunitySetCardProps) {
  const { t } = useTranslation()
  const isBusy = !!(isCreatingSet || isGenerating)
  const isInteractive = !!onClick && canCreate && !isBusy

  const actionLabel = isCreatingSet
    ? t("common.creating")
    : isGenerating
      ? t("opportunities.generating")
      : t("opportunities.newSet")

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onClick={isInteractive ? onClick : undefined}
            onKeyDown={(e) => {
              if (isInteractive && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault()
                onClick?.()
              }
            }}
            className={cn(
              "group/onboarding relative flex h-full min-h-40 w-full flex-col rounded-md bg-card p-3.5 transition-colors duration-200 ease-out",
              isInteractive
                ? "cursor-pointer hover:bg-primary/[0.03]"
                : !canCreate
                  ? "cursor-not-allowed opacity-60"
                  : isBusy
                    ? "cursor-wait opacity-75"
                    : "",
            )}
          >
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full text-border transition-colors duration-200 ease-out group-hover/onboarding:text-primary"
              aria-hidden
            >
              <rect
                width="100%"
                height="100%"
                rx="12"
                ry="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray="4 6"
              />
            </svg>

            <div className="mb-4 flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background transition-colors duration-200 ease-out group-hover/onboarding:border-primary/40 group-hover/onboarding:bg-primary/5">
                <Sparkles className="h-5 w-5 text-muted-foreground transition-colors duration-200 ease-out group-hover/onboarding:text-primary" />
              </div>
              <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors duration-200 ease-out group-hover/onboarding:text-primary">
                {t("common.new")}
              </span>
            </div>

            <h3 className="mb-1.5 text-sm font-semibold text-foreground/65">
              {t("opportunities.noSetsTitle")}
            </h3>
            <p className="mb-4 text-sm text-muted-foreground/75">
              {t("opportunities.noSetsDescription")}
            </p>

            <ul className="mb-4 space-y-2">
              <li className="flex items-center gap-2 text-xs text-muted-foreground">
                <BarChart2 className="h-3.5 w-3.5 shrink-0 transition-colors duration-200 ease-out group-hover/onboarding:text-primary/70" />
                {t("opportunities.noSetsFeature1")}
              </li>
              <li className="flex items-center gap-2 text-xs text-muted-foreground">
                <Zap className="h-3.5 w-3.5 shrink-0 transition-colors duration-200 ease-out group-hover/onboarding:text-primary/70" />
                {t("opportunities.noSetsFeature2")}
              </li>
              <li className="flex items-center gap-2 text-xs text-muted-foreground">
                <Layers className="h-3.5 w-3.5 shrink-0 transition-colors duration-200 ease-out group-hover/onboarding:text-primary/70" />
                {t("opportunities.noSetsFeature3")}
              </li>
            </ul>

            {showAction && onClick && (
              <span className="mt-auto flex items-center gap-1.5 text-sm text-muted-foreground transition-colors duration-200 ease-out group-hover/onboarding:text-primary">
                <span className="text-base leading-none">+</span>
                {actionLabel}
              </span>
            )}
          </div>
        </TooltipTrigger>
        {!canCreate && !isBusy && (
          <TooltipContent>
            <p>{t("common.noPermission")}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  )
}
